import { injectable } from 'inversify';

@injectable()
export class AssessmentSummaryHelper {
  private NORMAL_KEY = 'ID_MODULE_RESULT_NORMAL';
  private ABNORMAL_KEY = 'ID_MODULE_RESULT_ABNORMAL';

  moduleSkipped = (module: any): boolean => {
    return module?.skipModuleFlag === true;
  };

  symptomsPresentSummary = (symptomsChecklist: any): string => {
    if (!symptomsChecklist) {
      return '';
    }

    if (this.moduleSkipped(symptomsChecklist)) {
      return '-';
    }

    let symptomsSeverity: number = 0;

    for (let i = 0; i < Object.entries(symptomsChecklist).length; i++) {
      const [key, val] = Object.entries(symptomsChecklist)[i];
      if (typeof val === 'string') {
        symptomsSeverity += parseInt(val, 10);
      }
    }

    return symptomsSeverity.toString();
  };

  symptomsSeveritySummary = (symptomsChecklist: any): string => {
    if (!symptomsChecklist) {
      return '';
    }

    if (this.moduleSkipped(symptomsChecklist)) {
      return '-';
    }

    let symptomsPresent: number = 0;

    for (let i = 0; i < Object.entries(symptomsChecklist).length; i++) {
      const [key, val] = Object.entries(symptomsChecklist)[i];
      if (val !== '0') {
        symptomsPresent += 1;
      }
    }

    return symptomsPresent.toString();
  };

  private validSymptomKeyNames: string[] = [
    'symptomHeadache',
    'symptomPressureInHead',
    'symptomNeckPain',
    'symptomNauseaOrVomiting',
    'symptomDizziness',
    'symptomBlurredVision',
    'symptomBalanceProblems',
    'symptomSensitivityToLight',
    'symptomSensitivityToNoise',
    'symptomFeelingSlowedDown',
    'symptomFeelingLikeInFog',
    'symptomDontFeelRight',
    'symptomDifficultyConcentrating',
    'symptomDifficultyRemembering',
    'symptomFatigueOrLowEnergy',
    'symptomConfusion',
    'symptomDrowsiness',
    'symptomTroubleFallingAsleep',
    'symptomMoreEmotional',
    'symptomIrritability',
    'symptomSadness',
    'symptomNervousOrAnxious',
  ];

  symptomsIsNormative = (symptomsChecklist: any): boolean => {
    if (symptomsChecklist) {
      let symptomsPresent: number = 0;
      let symptomsSeverity: number = 0;

      for (
        let i: number = 0;
        i < Object.entries(symptomsChecklist).length;
        i++
      ) {
        const [key, val] = Object.entries(symptomsChecklist)[i];
        if (this.validSymptomKeyNames.includes(key)) {
          if (val !== 0) {
            symptomsPresent += 1;
            symptomsSeverity += val as number;
          }
        }
      }

      return symptomsPresent + symptomsSeverity === 0;
    }

    return false;
  };

  tandemGaitIsNormative = (balanceTandemGait: any): boolean => {
    return !!balanceTandemGait?.patientCanPerformTandemGaitNormally;
  };

  balanceDoubleLegStanceIsNormative = (
    balanceDoubleLegStanceErrors: number,
  ): boolean => {
    return balanceDoubleLegStanceErrors !== undefined &&
      balanceDoubleLegStanceErrors !== null
      ? balanceDoubleLegStanceErrors === 0
      : false;
  };

  balanceSingleLegStanceIsNormative = (
    balanceSingleLegStanceErrors: number,
  ): boolean => {
    return balanceSingleLegStanceErrors !== undefined &&
      balanceSingleLegStanceErrors !== null
      ? balanceSingleLegStanceErrors === 0
      : false;
  };

  balanceTandemStanceIsNormative = (
    balanceTandemStanceErrors: number,
  ): boolean => {
    return balanceTandemStanceErrors !== undefined &&
      balanceTandemStanceErrors !== null
      ? balanceTandemStanceErrors === 0
      : false;
  };

  neckExamIsNormative = (neckExam: any): boolean => {
    if (neckExam) {
      if (
        !neckExam?.neckExamTenderness &&
        !neckExam?.neckExamPain &&
        !neckExam?.neckExamPainFreeMotion
      )
        return true;
    }
    return false;
  };

  neuroExamIsNormative = (neurologicalExam: any): boolean => {
    if (neurologicalExam) {
      if (
        neurologicalExam?.neuroExamPainFreePassiveCervicalSpineMovement ||
        neurologicalExam?.neuroExamCanPerformCoordinationExamNormally ||
        neurologicalExam?.neuroExamCanReadAloudAndFollowInstructions ||
        neurologicalExam?.neuroExamCanLookSideToSideUpAndDownWithoutDoubleVision
      )
        return true;
    }
    return false;
  };

  gcsIsNormative = (glasgowComaScale: any): boolean => {
    if (this.moduleSkipped(glasgowComaScale)) {
      return false;
    }

    let score: number = 0;
    if (glasgowComaScale) {
      if (glasgowComaScale?.gcsEyeResponse) {
        switch (glasgowComaScale?.gcsEyeResponse) {
          case 'SPONTANEOUSLY':
            score += 4;
            break;
          case 'TO_SPEECH':
            score += 3;
            break;
          case 'TO_PAIN':
            score += 2;
            break;
          case 'NO_RESPONSE':
            score += 1;
            break;
          default:
            break;
        }
      }

      if (glasgowComaScale?.gcsVerbalResponse) {
        switch (glasgowComaScale?.gcsVerbalResponse) {
          case 'ORIENTED_TO_TIME_PLACE_AND_PERSON':
            score += 5;
            break;
          case 'CONFUSED':
            score += 4;
            break;
          case 'INAPPROPRIATE_WORDS':
            score += 3;
            break;
          case 'INCOMPREHENSIBLE_SOUNDS':
            score += 2;
            break;
          case 'NO_RESPONSE':
            score += 1;
            break;
          default:
            break;
        }
      }

      if (glasgowComaScale?.gcsMotorResponse) {
        switch (glasgowComaScale?.gcsMotorResponse) {
          case 'OBEYS_COMMANDS':
            score += 6;
            break;
          case 'MOVES_TO_LOCALISED_PAIN':
            score += 5;
            break;
          case 'FLEXION_WITHDRAWAL_FROM_PAIN':
            score += 4;
            break;
          case 'ABNORMAL_FLEXION_DECORTICATE':
            score += 3;
            break;
          case 'ABNORMAL_EXTENSION_DECEREBRATE':
            score += 2;
            break;
          case 'NO_RESPONSE':
            score += 1;
            break;
          default:
            break;
        }
      }
    }
    return score >= 15;
  };

  immMemIsNormative = (im: any): boolean => {
    let count = 0;
    if (im?.immediateMemoryTrial1Response) {
      const immediateMemoryTrial1ResponseLen = Object.keys(
        im?.immediateMemoryTrial1Response,
      ).length;
      if (immediateMemoryTrial1ResponseLen > 0) {
        count += immediateMemoryTrial1ResponseLen;
      }
    }
    if (im?.immediateMemoryTrial2Response) {
      const immediateMemoryTrial2ResponseLen = Object.keys(
        im?.immediateMemoryTrial2Response,
      ).length;
      if (immediateMemoryTrial2ResponseLen > 0) {
        count += immediateMemoryTrial2ResponseLen;
      }
    }
    if (im?.immediateMemoryTrial3Response) {
      const immediateMemoryTrial3ResponseLen = Object.keys(
        im?.immediateMemoryTrial3Response,
      ).length;
      if (immediateMemoryTrial3ResponseLen > 0) {
        count += immediateMemoryTrial3ResponseLen;
      }
    }

    return count >= 16;
  };

  digitsIsNormative(digitsBackwards: any): boolean {
    if (digitsBackwards?.sequencesRecalled) {
      let correctResponses: number = 0;
      for (
        let i = 0;
        i < Object.entries(digitsBackwards?.sequencesRecalled).length;
        i++
      ) {
        const [key, val] = Object.entries(digitsBackwards?.sequencesRecalled)[
          i
        ];
        if (val === 'Correct') {
          correctResponses += 1;
        }
      }

      if (correctResponses >= 4) {
        return true;
      }
    }

    return false;
  }
  monthsIsNormative = (monthsInReverse: any): boolean => {
    return monthsInReverse?.monthsInReverseScore === 1;
  };

  delayedRecallIsNormative = (delayedRecall: any): boolean => {
    let count = 0;
    if (delayedRecall?.wordsRecalled) {
      const delayedRecallResponseLen = Object.keys(
        delayedRecall?.wordsRecalled,
      ).length;
      if (delayedRecallResponseLen > 0) {
        count += delayedRecallResponseLen;
      }
    }
    return count >= 4;
  };

  vomsIsNormative = (): boolean => {
    return true;
  };

  comments = (module: any, comment: string) => {
    if (!module) {
      return '';
    }

    if (this.moduleSkipped(module)) {
      return module?.skipModuleComments || '-';
    }

    if (comment) {
      return comment;
    }

    return '-';
  };

  summary = (module: any, isNormative: boolean): string => {
    if (!module) {
      return '';
    }

    if (this.moduleSkipped(module)) {
      return '';
    }

    return isNormative ? this.NORMAL_KEY : this.ABNORMAL_KEY;
  };
}
